import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success('Welcome back!');
      navigate(u?.role === 'admin' ? '/admin' : '/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-12 blueprint-grid"
      style={{ backgroundColor: '#F5F2EC' }}
      data-testid="login-page"
    >
      <div
        className="w-full max-w-md bg-white border border-[var(--c-line)] shadow-[0_24px_60px_-24px_rgba(43,76,59,0.18)]"
        style={{ borderRadius: 6 }}
      >
        <div className="px-10 pt-10 pb-8">
          {/* Brand mark */}
          <div className="flex flex-col items-center gap-3">
            <img
              src="/ecotech-logo.png"
              alt="EcoTech"
              style={{ height: 56, width: 'auto', objectFit: 'contain' }}
            />
            <p
              className="text-sm mt-3"
              style={{ color: 'var(--c-ink-2)', fontFamily: 'IBM Plex Sans, sans-serif' }}
            >
              Embodied Carbon Intelligence Portal
            </p>
          </div>

          {/* Heading */}
          <div className="mt-10">
            <h1
              className="text-[28px] font-bold tracking-[0.04em]"
              style={{
                fontFamily: 'Chivo, sans-serif',
                color: 'var(--c-ink)',
                letterSpacing: '0.06em',
              }}
            >
              SIGN IN
            </h1>
            <p
              className="text-sm mt-2"
              style={{ color: 'var(--c-ink-2)' }}
            >
              Enter your credentials to continue
            </p>
            <div className="hair-divider mt-6" />
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="mt-7 space-y-5">
            <div>
              <label
                htmlFor="email"
                className="block text-[11px] font-semibold uppercase tracking-[0.18em] mb-2"
                style={{ color: 'var(--c-ink-2)' }}
              >
                Email Address
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                required
                data-testid="login-email-input"
                className="w-full px-4 py-3 text-[15px] outline-none transition-all"
                style={{
                  backgroundColor: 'var(--c-pine-soft)',
                  border: '1px solid transparent',
                  borderRadius: 6,
                  color: 'var(--c-ink)',
                  fontFamily: 'IBM Plex Sans, sans-serif',
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = 'var(--c-pine)';
                  e.target.style.backgroundColor = '#FFFFFF';
                  e.target.style.boxShadow = '0 0 0 3px rgba(43, 76, 59, 0.12)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'transparent';
                  e.target.style.backgroundColor = 'var(--c-pine-soft)';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>

            <div>
              <label
                htmlFor="password"
                className="block text-[11px] font-semibold uppercase tracking-[0.18em] mb-2"
                style={{ color: 'var(--c-ink-2)' }}
              >
                Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                data-testid="login-password-input"
                className="w-full px-4 py-3 text-[15px] outline-none transition-all"
                style={{
                  backgroundColor: 'var(--c-pine-soft)',
                  border: '1px solid transparent',
                  borderRadius: 6,
                  color: 'var(--c-ink)',
                  fontFamily: 'IBM Plex Sans, sans-serif',
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = 'var(--c-pine)';
                  e.target.style.backgroundColor = '#FFFFFF';
                  e.target.style.boxShadow = '0 0 0 3px rgba(43, 76, 59, 0.12)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'transparent';
                  e.target.style.backgroundColor = 'var(--c-pine-soft)';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>

            {/* Remember + Forgot */}
            <div className="flex items-center justify-between pt-1">
              <label
                htmlFor="remember"
                className="flex items-center gap-2 cursor-pointer text-sm"
                style={{ color: 'var(--c-ink-2)' }}
                data-testid="remember-me-label"
              >
                <input
                  id="remember"
                  type="checkbox"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                  className="w-4 h-4 cursor-pointer"
                  style={{ accentColor: 'var(--c-pine)' }}
                  data-testid="remember-me-checkbox"
                />
                <span>Remember me</span>
              </label>
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  toast.info('Password reset is not yet available. Contact your administrator.');
                }}
                className="text-sm font-medium hover:underline"
                style={{ color: 'var(--c-pine)' }}
                data-testid="forgot-password-link"
              >
                Forgot password?
              </a>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full font-semibold text-[15px] py-3 mt-2 transition-all"
              style={{
                backgroundColor: loading ? 'var(--c-pine-2)' : 'var(--c-pine)',
                color: '#FFFFFF',
                borderRadius: 6,
                letterSpacing: '0.02em',
                cursor: loading ? 'not-allowed' : 'pointer',
                boxShadow: '0 6px 16px -6px rgba(43, 76, 59, 0.45)',
              }}
              onMouseEnter={(e) => {
                if (!loading) e.currentTarget.style.backgroundColor = 'var(--c-pine-2)';
              }}
              onMouseLeave={(e) => {
                if (!loading) e.currentTarget.style.backgroundColor = 'var(--c-pine)';
              }}
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Signing in...
                </span>
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          {/* Divider + footer */}
          <div className="hair-divider mt-8" />
          <div className="pt-5 flex items-center justify-between">
            <p className="text-[11px]" style={{ color: 'var(--c-ink-3)' }}>
              © 2026 Switzel Ecotech — Secure Portal
            </p>
            <Link
              to="/register"
              className="text-[12px] font-medium hover:underline"
              style={{ color: 'var(--c-pine)' }}
              data-testid="register-link"
            >
              Create account →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
